import { Injectable, NgZone } from '@angular/core';

/**
 * Abilita lo scorrimento orizzontale "drag-to-scroll" (tieni premuto e trascina)
 * su TUTTE le tabelle del portale, presenti e future.
 *
 * Funziona tramite delega di eventi a livello di document: alla pressione del mouse
 * si risale al più vicino contenitore `.kcs-datatable-scroll-wrapper` scrollabile e,
 * se il click NON è su un elemento interattivo (input/select/button/link/checkbox...),
 * si avvia il trascinamento aggiornando `scrollLeft`.
 *
 * Va inizializzato una sola volta (AppComponent.ngOnInit → `init()`).
 */
@Injectable({ providedIn: 'root' })
export class TableDragScrollService {
	private wrapper: HTMLElement | null = null;
	private startX = 0;
	private startScrollLeft = 0;
	private dragging = false;
	private moved = false;

	/** selettore degli elementi su cui NON iniziare il drag (restano cliccabili/selezionabili) */
	private static readonly INTERACTIVE = 'input, select, textarea, button, a, [contenteditable], .p-select, .p-datepicker, .dropdown, .kcs-togglebutton, .kcs-togglelink, [role="tab"], .p-tab';

	constructor(private zone: NgZone) {}

	init(): void {
		// Fuori da Angular: sono listener DOM ad alta frequenza, non serve change detection.
		this.zone.runOutsideAngular(() => {
			document.addEventListener('mousedown', this.onMouseDown, true);
			document.addEventListener('mousemove', this.onMouseMove, true);
			document.addEventListener('mouseup', this.onMouseUp, true);
			// evita che un drag concluso scateni un click "fantasma" su una riga
			document.addEventListener('click', this.onClickCapture, true);
		});
	}

	private onMouseDown = (e: MouseEvent): void => {
		if (e.button !== 0) return; // solo tasto sinistro
		const target = e.target as HTMLElement;
		if (!target) return;
		if (target.closest(TableDragScrollService.INTERACTIVE)) return; // lascia interagire

		// Trova il più vicino contenitore scrollabile ORIZZONTALMENTE (qualsiasi classe):
		// .kcs-datatable-scroll-wrapper, wrapper di p-table, .table-responsive, ecc.
		const wrapper = this.findHorizontalScrollable(target);
		if (!wrapper) return;

		this.wrapper = wrapper;
		this.startX = e.pageX;
		this.startScrollLeft = wrapper.scrollLeft;
		this.dragging = true;
		this.moved = false;
		wrapper.classList.add('kcs-dragging');
	};

	/** Risale gli antenati e ritorna il primo elemento che scorre in orizzontale (overflow-x auto/scroll + overflow reale). */
	private findHorizontalScrollable(el: HTMLElement): HTMLElement | null {
		let node: HTMLElement | null = el;
		while (node && node !== document.body && node !== document.documentElement) {
			if (node.scrollWidth - node.clientWidth > 1) {
				const ox = getComputedStyle(node).overflowX;
				if (ox === 'auto' || ox === 'scroll') return node;
			}
			node = node.parentElement;
		}
		return null;
	}

	private onMouseMove = (e: MouseEvent): void => {
		if (!this.dragging || !this.wrapper) return;
		const delta = e.pageX - this.startX;
		if (Math.abs(delta) > 3) this.moved = true;
		this.wrapper.scrollLeft = this.startScrollLeft - delta;
		e.preventDefault();
	};

	private onMouseUp = (): void => {
		if (!this.dragging) return;
		this.dragging = false;
		this.wrapper?.classList.remove('kcs-dragging');
		this.wrapper = null;
	};

	private onClickCapture = (e: MouseEvent): void => {
		// se l'utente ha trascinato, annulla il click risultante (evita selezione riga involontaria)
		if (this.moved) {
			e.stopPropagation();
			e.preventDefault();
			this.moved = false;
		}
	};
}
